import { useEffect, useSyncExternalStore } from 'react';

/**
 * The Chats SPLIT view's list-column width, remembered per browser. In `split`
 * mode (`useChatsLayout`) the conversation panel takes whatever is left after
 * this — `CHATS_DOCK_WIDTH` in `use-chats-layout.ts` is `100vw - --sidebar-w -
 * this` — so dragging the boundary between the list and the open conversation
 * grows one at the other's expense, exactly like `use-chat-dock-width.ts` does
 * for the floating chat dock elsewhere in the app. Kept as its own store (not
 * reusing that one) because the two panels resize independently and default to
 * different widths.
 *
 * Web-only: on `stacked` (narrower) widths Chats is list→conversation
 * navigation, one screen at a time, with nothing to split — there is nothing
 * to mirror on native, which is always one screen.
 */
const STORAGE_KEY = 'symphony-chats-list-width';

/**
 * The width before anyone has dragged — and the width Figma draws.
 *
 * The desktop frame ("↪ [New] Chat on side ✅" → `[Desktop] New Chat`, 18693:74548) is 1920 wide and
 * lays out rail 260 + chats column **814** + conversation 846. This used to open at 360 and cap at 560,
 * so the drawn column was not merely un-defaulted but UNREACHABLE — no amount of dragging got there,
 * and the redesigned row (a 734-wide content box) was being squeezed into less than half its space.
 * Reported as "it looks like an iPad and not desktop".
 *
 * A window too narrow for 814 is handled by `viewportCap` below rather than by a smaller default, so
 * the list is as wide as the design wherever the design fits and degrades smoothly where it does not.
 */
export const CHATS_LIST_DEFAULT_WIDTH = 814;
export const CHATS_LIST_MIN_WIDTH = 280;
export const CHATS_LIST_MAX_WIDTH = 814;

/** Keep at least this much of the transcript visible beside the list. */
const MIN_VISIBLE_CHAT = 420;

/**
 * The rail's live width, which AppFrame mirrors onto :root as --sidebar-w.
 *
 * **The fallback is the rail's own CSS default (280), never 0**, because the variable is genuinely
 * absent for the first frames: `hydrateOnce` runs before AppFrame has published it. Falling back to 0
 * made `viewportCap` believe the whole viewport was free, so on a 1280 window the 814 default was
 * accepted and left 206px for the conversation — half of the `MIN_VISIBLE_CHAT` this cap exists to
 * guarantee. 280 is the WIDE rail, so an over-estimate: a collapsed rail only ever frees more room,
 * and the re-clamp below refines it the moment the real value lands.
 */
const RAIL_FALLBACK_WIDTH = 280;
function railWidth(): number {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return RAIL_FALLBACK_WIDTH;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-w');
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : RAIL_FALLBACK_WIDTH;
}

export function clampChatsListWidth(width: number): number {
  if (!Number.isFinite(width)) return CHATS_LIST_DEFAULT_WIDTH;
  const viewportCap =
    typeof window !== 'undefined'
      ? Math.max(CHATS_LIST_MIN_WIDTH, window.innerWidth - railWidth() - MIN_VISIBLE_CHAT)
      : CHATS_LIST_MAX_WIDTH;
  return Math.min(CHATS_LIST_MAX_WIDTH, viewportCap, Math.max(CHATS_LIST_MIN_WIDTH, Math.round(width)));
}

// SSR and the first client render both read the default, so the server HTML and
// the hydrated tree agree; the remembered value is applied in an effect after mount.
let current = CHATS_LIST_DEFAULT_WIDTH;
/**
 * The width last ASKED for — the remembered value, a drag, or the default — before clamping.
 *
 * Re-clamping has to start from this, never from `current`: clamping is monotonic, so feeding the
 * already-clamped value back in can only ever shrink it further. That is what made the first attempt
 * at the rail fix one-way — a list clamped to 580 against the 280 fallback stayed 580 once the real
 * (collapsed, 57) rail landed, instead of expanding to the ~803 that then fits.
 */
let requested = CHATS_LIST_DEFAULT_WIDTH;
let hydrated = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function setChatsListWidth(width: number): void {
  if (Number.isFinite(width)) requested = width;
  const next = clampChatsListWidth(width);
  if (next === current) return;
  current = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(current));
  } catch {
    // private mode / storage disabled — the width just won't persist.
  }
  emit();
}

export function resetChatsListWidth(): void {
  setChatsListWidth(CHATS_LIST_DEFAULT_WIDTH);
}

/**
 * Recompute from the REQUESTED width rather than the current one, so the list both shrinks when the
 * room goes and returns to what was asked for when it comes back — a window widening, the rail
 * collapsing, or `--sidebar-w` simply becoming readable for the first time.
 */
function reclamp(): void {
  const next = clampChatsListWidth(requested);
  if (next === current) return;
  current = next;
  emit();
}

function hydrateOnce(): void {
  if (hydrated) return;
  hydrated = true;
  let stored: number | null = null;
  let storedRaw: number | null = null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw == null ? Number.NaN : Number(raw);
    storedRaw = Number.isFinite(parsed) ? parsed : null;
    stored = raw == null ? null : clampChatsListWidth(parsed);
  } catch {
    stored = null;
  }
  // A viewport narrower than the remembered width re-clamps it down — but the remembered value is
  // kept UNCLAMPED as the request, so a later re-clamp against the real rail can widen it again.
  requested = storedRaw ?? CHATS_LIST_DEFAULT_WIDTH;
  const next = clampChatsListWidth(stored ?? current);
  if (next !== current) {
    current = next;
    emit();
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Test-only reset of the in-memory store. */
export function __resetChatsListWidthStore(): void {
  current = CHATS_LIST_DEFAULT_WIDTH;
  requested = CHATS_LIST_DEFAULT_WIDTH;
  hydrated = false;
}

export function useChatsListWidth(): number {
  const width = useSyncExternalStore(subscribe, () => current, () => CHATS_LIST_DEFAULT_WIDTH);
  useEffect(() => {
    hydrateOnce();
    // Re-clamp once AppFrame has published `--sidebar-w`. `hydrateOnce` necessarily runs before that
    // (this hook is what AppFrame reads to compute it), so the first clamp is made against the
    // fallback rail above; this second pass is what applies the REAL rail, whether it is the wide
    // 280, a collapsed rail, or a width the user dragged.
    const frame = requestAnimationFrame(reclamp);
    // Keep the width sane if the window shrinks below it — and let it grow back when it widens.
    const onResize = reclamp;
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  return width;
}
